import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CheckCircle, XCircle, Clock, BookOpen, Users, Building, BarChart2, Send, Copy } from '../../components/CustomIcons';

const TABS = ['Overview', 'Applications', 'Courses', 'Providers', 'Enrollments'];

function StatCard({ icon: Icon, value, label, color = 'var(--orange)' }) {
  return (
    <div className="card" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: `var(--bg-elevated)`, border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={17} style={{ color }} />
        </div>
      </div>
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', marginBottom: '4px' }}>{value}</div>
      <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}

export default function MarketplaceAdmin({ tab: initialTab }) {
  const { authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab || 'Overview');
  const [stats, setStats] = useState({});
  const [applications, setApplications] = useState([]);
  const [courses, setCourses] = useState([]);
  const [providers, setProviders] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteLinks, setInviteLinks] = useState({});

  useEffect(() => {
    setLoading(true);
    const requests = [authFetch('/marketplace/admin/stats')];
    if (activeTab === 'Applications') requests.push(authFetch('/marketplace/admin/applications'));
    if (activeTab === 'Courses') requests.push(authFetch('/marketplace/admin/courses'));
    if (activeTab === 'Providers') requests.push(authFetch('/marketplace/admin/providers'));
    if (activeTab === 'Enrollments') requests.push(authFetch('/marketplace/admin/enrollments'));

    Promise.all(requests).then(([statsRes, extraRes]) => {
      setStats(statsRes.data || {});
      if (activeTab === 'Applications') setApplications(extraRes?.data || []);
      if (activeTab === 'Courses') setCourses(extraRes?.data || []);
      if (activeTab === 'Providers') setProviders(extraRes?.data || []);
      if (activeTab === 'Enrollments') setEnrollments(extraRes?.data || []);
    }).catch(e => console.error('admin-fetch error:', e)).finally(() => setLoading(false));
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const reviewApp = async (appId, action) => {
    try {
      await authFetch(`/marketplace/admin/applications/${appId}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: action === 'approve' ? 'approved' : 'rejected' } : a));
    } catch (e) { console.error('review-app error:', e); }
  };

  const sendInvite = async (appId) => {
    try {
      const res = await authFetch(`/marketplace/admin/providers/${appId}/invite`, { method: 'POST' });
      const data = res.data || {};
      setInviteLinks(prev => ({ ...prev, [appId]: data.invite_link }));
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: 'approved' } : a));
    } catch (e) { console.error('invite error:', e); }
  };

  const reviewEnrollment = async (enrollmentId, status) => {
    try {
      await authFetch(`/marketplace/admin/enrollments/${enrollmentId}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      setEnrollments(prev => prev.map(e => e.id === enrollmentId ? { ...e, status } : e));
    } catch (e) { console.error('review-enrollment error:', e); }
  };

  const updateCourseStatus = async (courseId, status) => {
    try {
      await authFetch(`/marketplace/admin/courses/${courseId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, status } : c));
    } catch (e) { console.error('update-course-status error:', e); }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: { bg: 'var(--warning-bg)', color: 'var(--warning)' },
      approved: { bg: 'var(--success-bg)', color: 'var(--success)' },
      rejected: { bg: 'var(--error-bg)', color: 'var(--error)' },
      published: { bg: 'var(--success-bg)', color: 'var(--success)' },
      draft: { bg: 'var(--warning-bg)', color: 'var(--warning)' },
      archived: { bg: 'var(--bg-elevated)', color: 'var(--text-muted)' },
    };
    const s = styles[status] || styles.draft;
    return (
      <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '5px', background: s.bg, color: s.color }}>
        {status}
      </span>
    );
  };

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="marketplace-admin">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        <div style={{ marginBottom: '24px' }}>
          <div className="overline" style={{ marginBottom: '4px' }}>Admin</div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Marketplace Management</h1>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '2px', marginBottom: '24px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: '12px', width: 'fit-content' }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ padding: '8px 18px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', background: activeTab === tab ? 'var(--bg-surface)' : 'transparent', color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-muted)', transition: 'all 0.18s ease', boxShadow: activeTab === tab ? 'var(--shadow)' : 'none' }}
              data-testid={`admin-tab-${tab.toLowerCase()}`}>
              {tab}
            </button>
          ))}
        </div>

        {/* ── Overview Tab ── */}
        {activeTab === 'Overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px', marginBottom: '24px' }}>
              <StatCard icon={Building} value={stats.total_providers || 0} label="Approved Providers" />
              <StatCard icon={Clock} value={stats.pending_applications || 0} label="Pending Applications" color="var(--warning)" />
              <StatCard icon={BookOpen} value={stats.published_courses || 0} label="Published Courses" color="var(--success)" />
              <StatCard icon={Users} value={stats.total_enrollments || 0} label="Total Enrollments" color="#3B82F6" />
            </div>

            <div className="card" style={{ padding: '20px' }}>
              <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', margin: '0 0 12px 0' }}>Quick Actions</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {['Applications', 'Courses', 'Providers'].map(t => (
                  <button key={t} className="btn-secondary" onClick={() => setActiveTab(t)} style={{ fontSize: '13px' }}>
                    Manage {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Applications Tab ── */}
        {activeTab === 'Applications' && (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border-subtle)' }}>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)', margin: 0 }}>Provider Applications</h2>
            </div>
            {loading ? <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div> :
              applications.length === 0 ? <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>No applications found.</div> :
                applications.map((app, i) => (
                  <div key={app.id} style={{ padding: '18px 20px', borderBottom: i < applications.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                          <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', margin: 0 }}>{app.org_name}</p>
                          {statusBadge(app.status)}
                        </div>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>{app.contact_name} · {app.email}</p>
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 6px 0' }}>{app.category}</p>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {app.description}
                        </p>
                      </div>
                      {app.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          <button onClick={() => reviewApp(app.id, 'approve')}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'var(--success-bg)', color: 'var(--success)', fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                            data-testid={`approve-app-${app.id}`}>
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button onClick={() => reviewApp(app.id, 'reject')}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'var(--error-bg)', color: 'var(--error)', fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                            data-testid={`reject-app-${app.id}`}>
                            <XCircle size={14} /> Reject
                          </button>
                        </div>
                      )}
                      {app.status === 'approved' && !inviteLinks[app.id] && (
                        <button onClick={() => sendInvite(app.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'rgba(59,130,246,0.12)', color: '#3B82F6', fontSize: '13px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif', flexShrink: 0 }}
                          data-testid={`send-invite-${app.id}`}>
                          <Send size={14} /> Send Invitation
                        </button>
                      )}
                      {inviteLinks[app.id] && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', background: 'var(--bg-elevated)', borderRadius: '8px', padding: '6px 10px', maxWidth: '260px' }}>
                          <code style={{ fontSize: '11px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {inviteLinks[app.id]}
                          </code>
                          <button onClick={() => navigator.clipboard?.writeText(window.location.origin + inviteLinks[app.id])}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--orange)', padding: '2px' }}
                            data-testid={`copy-invite-${app.id}`}>
                            <Copy size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
            }
          </div>
        )}

        {/* ── Courses Tab ── */}
        {activeTab === 'Courses' && (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border-subtle)' }}>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)', margin: 0 }}>All Marketplace Courses</h2>
            </div>
            {loading ? <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div> :
              courses.map((c, i) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 20px', borderBottom: i < courses.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 2px 0' }}>{c.title}</p>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{c.provider_name} · {c.category} · {c.duration_hours}h</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{c.enrolled_count} enrolled</span>
                    {statusBadge(c.status)}
                    <select value={c.status} onChange={e => updateCourseStatus(c.id, e.target.value)}
                      style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '7px', color: 'var(--text-secondary)', fontSize: '12px', padding: '4px 8px' }}>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {/* ── Providers Tab ── */}
        {activeTab === 'Providers' && (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border-subtle)' }}>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)', margin: 0 }}>Approved Providers</h2>
            </div>
            {loading ? <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div> :
              providers.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 20px', borderBottom: i < providers.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'var(--orange-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Building size={18} style={{ color: 'var(--orange)' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 2px 0' }}>{p.name}</p>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{p.category} · {p.courses_count} courses · {p.learners_count} learners</p>
                  </div>
                  {statusBadge(p.status)}
                </div>
              ))
            }
          </div>
        )}

        {/* ── Enrollments Tab ── */}
        {activeTab === 'Enrollments' && (
          <div className="card" style={{ overflow: 'hidden' }} data-testid="enrollments-tab">
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)', margin: 0 }}>All Enrollments</h2>
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{enrollments.length} total</span>
            </div>
            {loading ? <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div> :
              enrollments.length === 0
                ? <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>No enrollments yet.</div>
                : enrollments.map((e, i) => (
                  <div key={e.id || i} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 20px', borderBottom: i < enrollments.length - 1 ? '1px solid var(--border-subtle)' : 'none', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 2px 0' }}>
                        {e.learner_name || 'Learner'}
                      </p>
                      <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
                        {e.learner_email} · {e.course_title}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                      {statusBadge(e.status)}
                      <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                        {e.created_at ? new Date(e.created_at).toLocaleDateString() : ''}
                      </span>
                      {e.status === 'pending' && (
                        <>
                          <button onClick={() => reviewEnrollment(e.id, 'approved')}
                            style={{ padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: 'var(--success-bg)', color: 'var(--success)', fontSize: '12px', fontWeight: 600 }}
                            data-testid={`approve-enrollment-${e.id}`}>
                            Approve
                          </button>
                          <button onClick={() => reviewEnrollment(e.id, 'rejected')}
                            style={{ padding: '5px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: 'var(--error-bg)', color: 'var(--error)', fontSize: '12px', fontWeight: 600 }}
                            data-testid={`reject-enrollment-${e.id}`}>
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
            }
          </div>
        )}
      </div>
    </div>
  );
}
